// apps/client/src/assets/builders/tank.ts — home aquarium (spec §9.10): glass .32 opacity per DESIGN.md "toy room".
// M1 playtest: tanks read as empty glass boxes (a small fish washed out behind a glass lid and a water volume). Now the tank is open at
// the top, its water is a sea-glass tint on the far walls and floor (behind the fish, never over it), and the fish, in the fish
// generator's own colours, fills most of the tank and swims gently from end to end.
import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { getItem, type FishEntry, type ShadowSize } from '@whiskerton/content';
import { tankFootprint } from '@whiskerton/shared/rules/aquarium';
import { toyMaterial } from '../../characters/doll';
import type { CodeBuilder } from '../service';
import { bakeMeshes } from '../../world/bake';
import { bakeFish, buildFish } from './fish';

const WATER = '#36AFC4', GLASS = '#E6F6FC', WOOD = '#B07A4F', RIM = '#C9A27E', GRAVEL = '#D9CC9A', WEED = '#6CBE6B';
/** How much of the water's length the fish spans, and how much of its height and width it may take. */
const FILL = 0.62, FILL_H = 0.85, FILL_W = 0.85;
const SWIM = 0.45;              // rad/s of the end-to-end swim
const hash = (s: string) => { let h = 2166136261; for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619); return h >>> 0; };

/** Four upright glass panes (no lid) around a w × d footprint, `h` tall from `y0`. */
function panes(w: number, d: number, y0: number, h: number): THREE.BufferGeometry {
  const sides = [[0, d / 2, 0, w], [0, -d / 2, Math.PI, w], [w / 2, 0, Math.PI / 2, d], [-w / 2, 0, -Math.PI / 2, d]] as const;
  const geos = sides.map(([x, z, ry, len]) => new THREE.PlaneGeometry(len, h).rotateY(ry).translate(x, y0 + h / 2, z));
  const out = mergeGeometries(geos, false)!;
  for (const g of geos) g.dispose();
  return out;
}

export function buildTank(fish: FishEntry, size?: number): THREE.Group {
  const [w, d] = tankFootprint((size ?? fish.shadow) as ShadowSize);
  const H = w > 1 || d > 1 ? 1.0 : 0.8;
  const g = new THREE.Group(); g.name = 'tank';
  const base = new THREE.Mesh(new RoundedBoxGeometry(w - 0.06, 0.14, d - 0.06, 3, 0.05), toyMaterial(WOOD));
  base.position.y = 0.07;
  // The water: its far walls and floor only (BackSide), so it tints what lies behind the fish and never washes the fish itself.
  const iw = w - 0.2, id = d - 0.2, wy0 = 0.205, wh = H - 0.32;   // from the gravel up, so the gravel shows through the tint
  const water = new THREE.Mesh(new THREE.BoxGeometry(iw, wh, id),
    new THREE.MeshStandardMaterial({ color: WATER, transparent: true, opacity: 0.72, roughness: 0.5, metalness: 0, depthWrite: false, side: THREE.BackSide }));
  water.name = 'water'; water.position.y = wy0 + wh / 2; water.renderOrder = -1;
  const gravel = new THREE.Mesh(new RoundedBoxGeometry(iw - 0.02, 0.06, id - 0.02, 2, 0.02), toyMaterial(GRAVEL));
  gravel.position.y = 0.17;
  const glass = new THREE.Mesh(panes(w - 0.1, d - 0.1, 0.12, H),
    new THREE.MeshStandardMaterial({ color: GLASS, transparent: true, opacity: 0.32, roughness: 0.08, metalness: 0, depthWrite: false, side: THREE.DoubleSide }));
  glass.name = 'glass';
  const rimMat = toyMaterial(RIM), rim = new THREE.Group(); rim.name = 'rim';      // the lip along the top edges reads as "a tank"
  for (const [x, z, rw, rd] of [[0, (d - 0.1) / 2, w - 0.06, 0.05], [0, -(d - 0.1) / 2, w - 0.06, 0.05], [(w - 0.1) / 2, 0, 0.05, d - 0.06], [-(w - 0.1) / 2, 0, 0.05, d - 0.06]] as const) {
    const bar = new THREE.Mesh(new RoundedBoxGeometry(rw, 0.05, rd, 2, 0.02), rimMat);
    bar.position.set(x, 0.12 + H, z); rim.add(bar);
  }
  // Two water weeds in the back corners.
  const weeds = new THREE.Group(); weeds.name = 'weeds';
  for (const sx of [-1, 1]) {
    const weed = new THREE.Mesh(new THREE.ConeGeometry(0.05, Math.min(0.4, wh * 0.6), 5), toyMaterial(WEED));
    weed.position.set(sx * (iw / 2 - 0.08), 0.2 + Math.min(0.4, wh * 0.6) / 2, -(id / 2 - 0.08));
    weeds.add(weed);
  }

  // The fish, centred on a pivot ('swimmer') so it turns about its middle; its long axis runs along the tank's long side.
  const f = bakeFish(buildFish(fish.look, fish.look.length, fish.id));   // two draws: the body and the tail that wags
  const along = d > w, long = along ? id : iw, short = along ? iw : id;
  const natural = new THREE.Box3().setFromObject(f), ns = natural.getSize(new THREE.Vector3());
  const k = Math.min((long * FILL) / ns.x, (wh * FILL_H) / ns.y, (short * FILL_W) / ns.z);
  f.scale.multiplyScalar(k);
  const c = natural.getCenter(new THREE.Vector3()).multiplyScalar(k);
  f.position.set(-c.x, -c.y, -c.z);
  const swimmer = new THREE.Group(); swimmer.name = 'swimmer';
  swimmer.add(f);
  const len = ns.x * k, tall = ns.y * k, mid = wy0 + wh / 2;
  const range = Math.max(0, (long - len) / 2 - 0.04), bob = Math.max(0, Math.min(0.03, (wh - tall) / 2 - 0.02));
  const turns = len <= short - 0.04;                  // room to turn round: a yaw; else a quick cartoon flip (mirror through 0)
  const phase = (hash(fish.id) % 628) / 100, tail = f.getObjectByName('tail');
  const pose = (t: number) => {
    const a = t * SWIM + phase, v = Math.cos(a), dir = THREE.MathUtils.clamp(v * 6, -1, 1);
    const x = Math.sin(a) * range;
    swimmer.position.set(along ? 0 : x, mid + Math.sin(t * 1.3 + phase) * bob, along ? x : 0);
    const yaw0 = along ? -Math.PI / 2 : 0;
    if (turns) { swimmer.rotation.y = yaw0 + (1 - dir) * Math.PI / 2; swimmer.scale.x = 1; }
    else { swimmer.rotation.y = yaw0; swimmer.scale.x = dir; }
    if (tail) tail.rotation.y = Math.sin(t * 7 + phase) * 0.35;
  };
  pose(0);
  const body = f.getObjectByName('body')!;
  body.onBeforeRender = () => pose(performance.now() / 1000);   // gentle swim, no system needed
  // The stand, gravel, rim and weeds never move: one draw (spec §8.7), not eight.
  const fixed = new THREE.Group();
  fixed.add(base, gravel, weeds, rim);
  const [stand] = bakeMeshes(fixed, { castShadow: true });
  stand!.name = 'stand';
  g.add(stand!, water, swimmer, glass);
  g.userData.footprint = [w, d];
  return g;
}
export const builder: CodeBuilder = {
  id: 'tank',
  build: (p) => buildTank(getItem(String(p.catalogId)) as FishEntry, typeof p.size === 'number' ? p.size : undefined),
};
